import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { SectionHeading } from './SectionHeading';
import { FLOOR_PLANS, ACCOMMODATION, fadeIn, fadeUp } from './data';

const PlanViewer = ({ plan, onClose }) => {
  const [zoomed, setZoomed] = useState(false);
  return (
    <Dialog open={Boolean(plan)} onOpenChange={(o) => { if (!o) { setZoomed(false); onClose(); } }}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[92vh] p-0 bg-[#e8e6df] border-none flex flex-col" data-testid="plan-viewer">
        <div className="flex items-center justify-between gap-4 px-5 py-3 bg-[#1a1e1a] pr-14">
          <DialogTitle className="text-white text-sm tracking-[0.12em] uppercase font-normal">{plan?.label}</DialogTitle>
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => setZoomed(!zoomed)} className="text-white/85 hover:text-white inline-flex items-center gap-2 text-sm" data-testid="plan-zoom-toggle">
              {zoomed ? <ZoomOut size={16} /> : <ZoomIn size={16} />}{zoomed ? 'Fit' : 'Zoom'}
            </button>
            <a href={plan?.src} target="_blank" rel="noopener noreferrer" download className="text-white/85 hover:text-white inline-flex items-center gap-2 text-sm" data-testid="plan-download">
              <Download size={16} />Download
            </a>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {plan && (
            <img
              src={plan.src}
              alt={plan.alt}
              onClick={() => setZoomed(!zoomed)}
              className={zoomed ? 'max-w-none w-[220%] md:w-[180%] cursor-zoom-out' : 'w-full h-full object-contain cursor-zoom-in p-4'}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const FloorPlans = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="plans" className="py-20 md:py-28 bg-[#3d4f3c]" data-testid="floorplans-section">
      <SectionHeading eyebrow="Plans">Floor plans and site</SectionHeading>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.dl {...fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="accommodation-summary">
          {ACCOMMODATION.map((a) => (
            <div key={a.level} className="border-t border-white/25 pt-5">
              <dt className="text-[#d6cdb4] text-xs tracking-[0.2em] uppercase mb-3">{a.level}</dt>
              <dd className="text-white/85 text-sm leading-relaxed">{a.rooms}</dd>
            </div>
          ))}
        </motion.dl>

        <div className="space-y-6">
          {FLOOR_PLANS.map((p, i) => (
            <motion.button
              key={p.src}
              type="button"
              {...fadeIn}
              onClick={() => setActive(p)}
              data-testid={`floorplan-${i}`}
              className="group relative block w-full bg-[#e8e6df] p-4 md:p-8 text-left"
            >
              <img src={p.src} alt={p.alt} className="w-full h-auto" />
              <span className="absolute top-3 right-3 inline-flex items-center gap-2 bg-[#1a1e1a] text-white text-xs tracking-[0.12em] uppercase px-3 py-2 group-hover:bg-[#3d4f3c] transition-colors">
                <Maximize2 size={14} />Tap to enlarge
              </span>
            </motion.button>
          ))}
        </div>
        <p className="text-white/70 text-sm mt-6 text-center">
          Plans show the full architectural design. Please confirm exactly what is included with the agent.
        </p>
      </div>
      <PlanViewer plan={active} onClose={() => setActive(null)} />
    </section>
  );
};
